import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import PortfolioDashboard from '../features/portfolio/PortfolioDashboard';
import { slugify, toArray } from '../utils/slugify';
import { useNavActions } from '../context/NavActionsContext';
import { apiFetch } from '../api/apiClient';

function GearIcon() {
  return (
    <svg
      width="20" height="20" viewBox="0 0 24 24"
      fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function PortfolioPage() {
  const { slug: slugParam } = useParams();
  const location = useLocation();
  const slug = slugParam || location.pathname.replace(/^\/portfolio\//, '').split('/')[0] || '';
  const navigate = useNavigate();
  const { registerNavRight } = useNavActions();

  const [portfolioId, setPortfolioId] = useState(null);
  const [resolving, setResolving] = useState(true);
  const [error, setError] = useState('');
  const [benchmarkTicker, setBenchmarkTicker] = useState(null);

  // Resolve slug → numeric portfolio ID + redirect legacy numeric URLs
  useEffect(() => {
    let isMounted = true;
    const resolve = async () => {
      try {
        setResolving(true);
        const res = await apiFetch('/api/portfolios');
        if (!res.ok) throw new Error('Failed to load portfolios.');
        const data = await res.json();
        const list = toArray(data);
        const match =
          list.find((p) => slugify(p.name) === slug) ||
          list.find((p) => String(p.id) === String(slug)) ||
          null;
        if (!isMounted) return;
        if (match) {
          setPortfolioId(String(match.id));
          setBenchmarkTicker(match.benchmarkTicker || null);
          const correctSlug = slugify(match.name);
          if (correctSlug && correctSlug !== slug) {
            navigate('/portfolio/' + correctSlug, { replace: true });
          }
        } else {
          setError('Portfolio not found.');
        }
      } catch (err) {
        if (isMounted) setError(err.message || 'Failed to resolve portfolio.');
      } finally {
        if (isMounted) setResolving(false);
      }
    };
    resolve();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Register gear icon in the navbar right slot (visible on both desktop and mobile)
  useEffect(() => {
    const unregister = registerNavRight(
      'portfolio-settings',
      'Portfolio settings',
      () => navigate('/portfolio/' + slug + '/settings'),
      () => <GearIcon />
    );
    return unregister;
  }, [registerNavRight, navigate, slug]);

  if (resolving) {
    return (
      <div style={pageStyle}>
        <p style={mutedStyle}>Loading portfolio...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <div style={errorBoxStyle}>{error}</div>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      {portfolioId && (
        <PortfolioDashboard
          portfolioId={portfolioId}
          portfolioSlug={slug}
          externalBenchmarkTicker={benchmarkTicker}
        />
      )}
    </div>
  );
}

const pageStyle = {
  display: 'grid',
  gap: '16px',
  minWidth: 0,
  width: '100%',
};

const errorBoxStyle = {
  padding: '12px 14px',
  borderRadius: '12px',
  background: 'rgba(220, 38, 38, 0.12)',
  color: '#fca5a5',
  border: '1px solid rgba(220, 38, 38, 0.35)',
  fontWeight: 600,
};

const mutedStyle = {
  margin: 0,
  color: 'var(--text-secondary)',
};

export default PortfolioPage;
